import { Button, Col, Form, InputGroup, Row, Alert, Table, OverlayTrigger, Tooltip, Popover, Modal } from "react-bootstrap";
import { forwardRef, useContext, useState, useRef, useEffect, useCallback } from "react";
import fieldDef, { defType } from "./FieldDef";
import { runInnerFunction } from "../../compatManager";
import { useAppSelector } from "../../redux/hooks";
import { lddStateObject } from "../../redux/reducer";
import { RPWDataReader, getDataReader } from "../../livedataFuncs";
import { NavigationContext } from "../../Context/NavigationContext";
import { NavObjectType } from "../navigationTypes";
import AnalyzerFuncs from "./analyzer";
import SearchBar from './SearchBar';
import { ThirdPartyDispatch } from "../ThirdPartyList/ThirdPartyFuncs";
import { useDispatch } from "react-redux";
import { RenAIImportFuncs } from "../../fileToolbar/RenAI/renaiFuncs";

type FieldProps = {
    type: "string" | "date" | "money",
    value: string | number,
    label: string,
    disabled: boolean,
    placeholder?: string
}

export type Branch = {
    LenderContactID: string | number;
    Branch: string
    Address?: string
}

export type Lender = {
    LDDLenderID: string | number, 
    LenderID: string | number, 
    Name: string, 
    LenderCode: string, 
    InstitutionNumber: string | number | null 
}

const Field = (props: FieldProps) => {
    const { type, value, placeholder } = props;
    let moneyVal = "";
    if (type === "money") {
        // Convert to number, round to 2 decimal places, then format
        const numValue = parseFloat(value.toString().replace(/[^0-9.]/g, ''));

        if(!isNaN(numValue)){
            const roundedValue = Math.round(numValue * 100) / 100;
            const parts = roundedValue.toString().split(".");
            moneyVal = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (parts[1] ? "." + parts[1].padEnd(2, "0").substring(0, 2) : ".00");
        }
    }
    return (
        <>
            {type === "money" &&
                <InputGroup.Text>
                    <i className="fa-solid fa-dollar-sign form-prefix" />
                </InputGroup.Text>
            }

            {type === "date" &&
                <InputGroup.Text>
                    <i className="fa-solid fa-calendar-days form-prefix" />
                </InputGroup.Text>
            }

            <Form.Control disabled value={type === "money" ? moneyVal : value} placeholder={placeholder}></Form.Control>
        </>
    )
}

type IProps = {
    data: object,
    selectOneField: (select: boolean) => void,
    fileLocation?: string,
    onLenderChange?: (hasValidLender: boolean) => void
};

const initImportField = (data) => {
    const importField = {};
    fieldDef.Lender.forEach(checkAndSelect);
    fieldDef.MortgageInfo.forEach(checkAndSelect);
    fieldDef.ChargeProv.forEach(checkAndSelect);
    function checkAndSelect(field: defType) {
        if (data[field.attribute]) {
            importField[field.attribute] = data[field.attribute];
        }
    }
    importField["branch"] = data["branch"];
    if (data["Term"]) importField["Term"] = data["Term"]
    return importField;
}

export const Import = forwardRef<HTMLButtonElement, IProps>((props: IProps, ImportRef) => {
    const mainBranch = "Main Branch"
    const mainLender = props.data['Lender'];
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const nav = useContext(NavigationContext);
    const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
    const [importField, setImportField] = useState(initImportField(props.data));
    const SESSION_INFO = useAppSelector((state: lddStateObject) => state.SessionInfo);
    const [selectedBranch, setSelectedBranch] = useState(mainBranch);
    const [selectedLender, setSelectedLender] = useState<string>(mainLender);
    const [selectedBranchID, setSelectedBranchID] = useState<number>(-1);
    const [showSearch, setShowSearch] = useState(false);
    const [showLenderSearch, setShowLenderSearch] = useState(false);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [lenders, setLenders] = useState<any[]>([]);
    const [searchBarKey, setSearchBarKey] = useState(0);
    const [lenderSearchBarKey, setLenderSearchBarKey] = useState(0);
    const [originalBranch, setOriginalBranch] = useState<Branch | string>(mainBranch);
    const [isLoadingBranches, setIsLoadingBranches] = useState(false);
    const [isLoadingLenders, setIsLoadingLenders] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        setOriginalBranch(props.data["branch"]['Branch'] || undefined);
    }, [props.data]);

    useEffect(() => {
        const isValidLender = selectedLender && selectedLender.trim() !== "";
        if (props.onLenderChange) {
            props.onLenderChange(isValidLender);
        }
    }, [selectedLender, props.onLenderChange]);

    const checkboxHandler = (checked: boolean, attribute: string) => {
        // console.log(e.target.name)
        const newState = { ...importField };
        if (checked) {
            newState[attribute] = props.data[attribute];
        } else {
            delete newState[attribute];
        }
        // console.log(newState)
        setImportField(newState);
        props.selectOneField(checked);
    }

    const isChecked = (attribute: string) => {
        return (importField[attribute] && importField[attribute] === props.data[attribute]);
    }

    const submitHandler = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const mortgageInfo = { ...importField };
        if (Number(props.data['LDDLenderID']) === -1) {
            if(props.data['Lender'].trim() > ""){
                let lenderName = props.data['Lender'];
                const lenderConfig = await AnalyzerFuncs.getLenderConfigByName(lenderName, SESSION_INFO);
                if(lenderConfig && Object.keys(lenderConfig).length > 0){
                    props.data['LDDLenderID'] = lenderConfig.LDDLenderID;
                    props.data['LenderID'] = lenderConfig.LenderID;
                    props.data['LenderProgramID'] = lenderConfig.LenderProgramID;
                    if (lenderConfig.InstitutionNumber){
                       mortgageInfo['InstitutionNumber'] = lenderConfig.InstitutionNumber;
                    }
                    if (lenderConfig.Name){
                        mortgageInfo['Lender'] = lenderConfig.Name;
                    }
                }
                else{
                    setShowModal(true);
                    return;
                }
            }
            else{
                setShowModal(true);
                return;
            }
        }
        mortgageInfo["LDDLenderID"] = props.data["LDDLenderID"];
        mortgageInfo["LenderID"] = props.data["LenderID"];
        mortgageInfo["LenderProgramID"] = props.data["LenderProgramID"];
        mortgageInfo["Analyzer"] = true;
        mortgageInfo["AnalyzeID"] = props.data["id"];
        if (selectedBranchID !== -1){
            const selectedBranch = await AnalyzerFuncs.getBranchDetails(selectedBranchID, SESSION_INFO);
            mortgageInfo["selectBranch"] = selectedBranch
        }
        runInnerFunction('ReactShell.reactSelectMortgage', mortgageInfo);

        // Log import info after successful import
        // Use LddDocLink from the autogen response data, fallback to fileLocation
        const lddDocLink = props.data['id'] || props.fileLocation;
        if (lddDocLink) {
            try {
                await RenAIImportFuncs.logImportInfo(
                    SESSION_INFO, 
                    DataReaderObj.getAppId(), 
                    lddDocLink, 
                    mortgageInfo, 
                    "Mortgage Instructions"
                );
            } catch (error) {
                console.error("[RenAI]: Error logging import info", error);
            }
        }

        // nav to mortgage tab
        const mortgages = DataReaderObj.getMortgageInformation();
        if (!mortgages || mortgages.length === 0) {
            return;
        }
        const tab: NavObjectType = {
            component: "main",
            url: "lfmaster.asp",
            params: {
                "tb": "TPAppXML",
                "formasp": "purfileedit",
                "id": DataReaderObj.getAppId(),
                "PSM": DataReaderObj.getPSM(),
            },
            label: mortgages[mortgages.length - 1].Lender,
            controller: {
                tabName: "Mortgages",
                tabNumber: mortgages.length,
            },
        };
        nav(tab);
        ThirdPartyDispatch(dispatch, "MortgageDropbox", false);
    }

    const RenderField = (field: defType) => {
        const [overlayWidth, setOverlayWidth] = useState(0);
        const fieldRef = useRef<HTMLDivElement>(null);

        const resetSearchBar = (setSearchBarKey) => {
            setSearchBarKey(prev => prev + 1);
        };

        const toggleSearch = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (!showSearch) {
                setIsLoadingBranches(true);
                try {
                    const fetchedBranches = await AnalyzerFuncs.getLenderBranches(props.data["LDDLenderID"], SESSION_INFO);
                    setBranches(fetchedBranches);
                } catch (error) {
                    console.error("Error fetching branches:", error);
                } finally {
                    setIsLoadingBranches(false);
                }
                setShowSearch(true);
            } else {
                setShowSearch(false);
                resetSearchBar(setSearchBarKey);
            }
        };

        const toggleLenderSearch = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowSearch(false);
            
            if (!showLenderSearch) {
                setIsLoadingLenders(true);
                try {
                    const fetchedLenders = await AnalyzerFuncs.getAllLendersInProvince(SESSION_INFO);
                    setLenders(fetchedLenders);
                } catch (error) {
                    console.error("Error fetching lenders:", error);
                } finally {
                    setIsLoadingLenders(false);
                }
                setShowLenderSearch(true);
            } else {
                setShowLenderSearch(false);
                resetSearchBar(setLenderSearchBarKey);
            }
        };

        const resetBranch = () => {
            const branchValue = typeof originalBranch === 'object' ? originalBranch.Branch || mainBranch : mainBranch;
            setSelectedBranch(branchValue);
            const newState = { ...importField };
            newState["branch"]["Branch"] = null;
            setSelectedBranchID(-1);
            setImportField(newState);
            setShowSearch(false);
        };

        const resetLender = () => {
            const lenderValue = mainLender;
            setSelectedLender(lenderValue);
            const newState = { ...importField };
            newState['Lender'] = lenderValue;
            newState['LDDLenderID'] = -1;
            newState['LenderID'] = -1;
            newState['LenderProgramID'] = 'CMASTER';
            newState['InstitutionNumber'] = 'CMASTER-N/A';
            props.data['LDDLenderID'] = -1;
            setImportField(newState);
            setShowLenderSearch(false);
            resetBranch();
        };

        useEffect(() => {
            if (fieldRef.current) {
                setOverlayWidth(fieldRef.current.offsetWidth);
            }
        }, []);

        const handleClose = useCallback(() => {
            setShowSearch(false);
            resetSearchBar(setSearchBarKey);
        }, []);

        const handleLenderClose = useCallback(() => {
            setShowLenderSearch(false);
            resetSearchBar(setLenderSearchBarKey);
        }, []);

        const handleBranchSelect = useCallback((selectedTerm: Branch) => {
            const branchName = selectedTerm['Branch'] ? `${selectedTerm['Branch']}: ${selectedTerm['Address']}` : selectedTerm['Address'];
            setSelectedBranch(branchName);
            const newState = { ...importField };
            if (branchName.indexOf(":") !== -1){
                const branchNameElements = branchName.split(":");
                if (branchNameElements.length > 1){
                    const branch = branchNameElements[0].trim();
                    const address = branchNameElements[1].trim();
                    newState["branch"]["Branch"] = branch;
                    newState["branch"]["MailingStreetAddress"] = address;
                }
                else{
                    newState["branch"]['Branch'] = branchName;
                }
            }
            else{
                newState["branch"]['Branch'] = branchName;
            }
            
            setImportField(newState);
            setShowSearch(false);
            resetSearchBar(setSearchBarKey);
        }, []);

        const handleLenderSelect = useCallback((lender: Lender) => {
            const lenderName = lender['Name'];
            props.data['LDDLenderID'] = lender['LDDLenderID'];
            props.data['LenderID'] = lender['LenderID'];
            props.data['LenderProgramID'] = lender['LenderProgramID'];
            setSelectedLender(lenderName);
            const newState = { ...importField };
            newState['Lender'] = lenderName;
            newState['LDDLenderID'] = lender['LDDLenderID'];
            newState['LenderID'] = lender['LenderID'];
            newState['LenderProgramID'] = lender['LenderProgramID'];
            if(lender['InstitutionNumber']){
                newState['InstitutionNumber'] = lender['InstitutionNumber'];
            }
            setImportField(newState);
            setShowLenderSearch(false);
            resetSearchBar(setLenderSearchBarKey);
            resetBranch();
        }, []);

        const popoverSearch = (
            <Popover style={{ maxWidth: 'none', width: `${overlayWidth-18}px` }}>
                <Popover.Body>
                    <SearchBar
                        key={searchBarKey}
                        onClose={handleClose}
                        searchValues={branches}
                        onSelect={handleBranchSelect}
                        setSelectedID={setSelectedBranchID}
                        searchType="branch"
                    />
                </Popover.Body>
            </Popover>
        );

        const popoverLenderSearch = (
            <Popover style={{ maxWidth: 'none', width: `${overlayWidth-18}px` }}>
                <Popover.Body>
                    <SearchBar
                        key={lenderSearchBarKey}
                        onClose={handleLenderClose}
                        searchValues={lenders}
                        onSelect={handleLenderSelect}
                        searchType="lender"
                    />
                </Popover.Body>
            </Popover>
        );

        const chooseLenderModal = (
            
            <Modal show={showModal} backdrop={false} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{`Lender Information ${props.data['Lender'].trim() > "" ? "Invalid" : "Missing"}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {props.data['Lender'].trim() > "" ? `The lender "${props.data['Lender']}" is invalid. Please select a valid lender to proceed with the Import.` : `No Lender chosen. Please select a valid lender to proceed with the Import.`}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowModal(false)}>
                        Agreed
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    
        return (
            <>
                <Row key={field.label} className="mb-2" ref={fieldRef}>
                    <Col>
                        <Form.Label>{field.label}</Form.Label>
                        <InputGroup>
                            {field.label === 'Branch' && (
                                <OverlayTrigger
                                    trigger="click"
                                    placement="bottom-start"
                                    show={showSearch}
                                    overlay={popoverSearch}
                                    rootClose={false}
                                >
                                    <Button className="btn-search" variant="primary" onClick={toggleSearch}>
                                        {field.label === 'Branch' && isLoadingBranches ? (
                                            <i className="fa-solid fa-spinner fa-spin"/>
                                        ):(
                                            <i className="fas fa-search"></i>
                                        )}
                                    </Button>
                                </OverlayTrigger>
                            )}

                            {field.label === 'Lender' &&(
                                <OverlayTrigger
                                    trigger="click"
                                    placement="bottom-start"
                                    show={showLenderSearch}
                                    overlay={popoverLenderSearch}
                                    rootClose={false}
                                >
                                    <Button className="btn-search" variant="primary" onClick={toggleLenderSearch}>
                                        {field.label === 'Lender' && isLoadingLenders ? (
                                            <i className="fa-solid fa-spinner fa-spin"/>
                                        ):(
                                            <i className="fas fa-search"></i>
                                        )}
                                    </Button>
                                </OverlayTrigger>
                            )}

                            <Field
                                label={field.label}
                                type={field.type}
                                value={field.label === 'Branch' ? selectedBranch : 
                                    (field.label === 'Lender' ? (selectedLender || props.data[field.attribute] || "") : 
                                    (props.data[field.attribute] ?? ""))}
                                disabled={field.label !== 'Branch' && field.label !== 'Lender'}
                                placeholder={field.label === 'Lender' ? "Please Select a Lender" : "no data found.."}
                            />

                            {
                                field.label === 'Branch' && selectedBranch !== mainBranch &&
                                <InputGroup.Text onClick={resetBranch}>
                                    <OverlayTrigger placement="bottom" delay={200} overlay={
                                        <Tooltip>Reset to Main Branch</Tooltip>}>
                                        <i className="fa-solid fa-rotate-left"></i>
                                    </OverlayTrigger>
                                </InputGroup.Text>
                            }

                            {
                                field.label === 'Lender' && props.data['LenderCode'] === 'cmaster' && selectedLender !== mainLender &&
                                <InputGroup.Text onClick={resetLender}>
                                    <OverlayTrigger placement="bottom" delay={200} overlay={
                                        <Tooltip>Reset Lender</Tooltip>}>
                                        <i className="fa-solid fa-rotate-left"></i>
                                    </OverlayTrigger>
                                </InputGroup.Text>
                            }

                            {!['Branch', 'Lender'].includes(field.label) && (
                                <InputGroup.Checkbox inline="true" type="checkbox" name={field.label}
                                    checked={isChecked(field.attribute)}
                                    onChange={e => checkboxHandler(e.target.checked, field.attribute)}
                                    disabled={!props.data[field.attribute]}
                                />
                            )}
                        </InputGroup>
                        {
                            field.label === 'Lender' && selectedLender.trim() === "" &&
                            <div style={{ color: 'red', marginTop: '5px' }}>Please select a Lender</div>
                        }
                    </Col>
                </Row>

                {chooseLenderModal}
            </>
        );
    };

    const ImportForm = () => {
        return (
            <>
                <Form onSubmit={submitHandler}>
                    <Row>
                        <Col className="col-6">
                            <h3>Lender</h3>
                            {fieldDef.Lender.map((field, index) => (
                                <div key={`lender-${field.label}-${index}`}>
                                    {RenderField(field)}
                                </div>
                            ))
                            }

                            <h3 style={{ marginTop: '50px' }}>Mortgage Information</h3>
                            {fieldDef.MortgageInfo.map((field, index) => (
                                <div key={`mortgage-${field.label}-${index}`}>
                                    {RenderField(field)}
                                </div>
                            ))
                            }
                        </Col>

                        <Col className="col-6">
                            <h3>Charge Provisions</h3>
                            {fieldDef.ChargeProv.map((field, index) => (
                                <div key={`charge-${field.label}-${index}`}>
                                    {RenderField(field)}
                                </div>
                            ))
                            }
                        </Col>
                    </Row>

                    <Button type="submit" ref={ImportRef} style={{ display: "none" }}></Button>
                </Form>
            </>
        )
    }

    const TopTable = () => {
        return (
            <>
                <Alert variant="warning">
                    Ensure all information matches with the Mortgage Instructions from your Lender. Then select the mortgage information you would like to import. <b>Existing mortgage details in your file will be overwritten.</b>
                    <p><b>Note:</b> This does not apply to the Lawyer, Borrower's Names or Property Address.</p>
                </Alert>                
                <Table bordered>
                    <thead>
                        <tr>
                            <th></th>
                            <th>Details on File</th>
                            <th>Generated Information</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {fieldDef.General.map((field) => {
                            const isDiff = !props.data[field.attribute] || DataReaderObj[field.RPWDataReader]() !== (props.data[field.attribute] ?? "");
                            return (
                                !(!props.data[field.attribute] && field.dontShowBlank) &&
                                <tr key={field.label}>
                                    <td>{field.label}</td>
                                    <td>{DataReaderObj[field.RPWDataReader]()}</td>
                                    <td>{props.data[field.attribute] ?? ""}</td>
                                    <td>
                                        {field.check &&
                                            (isDiff ?
                                                <OverlayTrigger placement="bottom" delay={200} overlay={
                                                    <Tooltip>The imported {field.label} does NOT match your file record.</Tooltip>}>
                                                    <i className="fa-solid fa-circle-exclamation status-warning"></i>
                                                </OverlayTrigger>
                                                :
                                                <OverlayTrigger placement="bottom" delay={200} overlay={
                                                    <Tooltip>The imported {field.label} matches your file record.</Tooltip>}>
                                                    <i className="fa-solid fa-circle-check status-success"></i>
                                                </OverlayTrigger>
                                            )
                                        }
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            </>
        )
    }

    return (
        <>
            <TopTable />
            <ImportForm />
        </>
    )
})